/**
 * Phase 8 · C-49.3 — Requisition detail header band.
 *
 * Replaces the legacy green strip on /requisitions/:id per §34.3.A:
 *   Title + sub-title · chip cluster ([Status][Priority][Employment type]
 *   [Location][Remote OK]) · TatChip right-aligned.
 *
 * Primary CTA stays in the right rail (see actionsEnd).
 */
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Requisition } from '@core/types';
import { REQUISITION_STATUS_LABELS } from '@core/constants/requisitionLifecycle';
import { Breadcrumb, type BreadcrumbItem } from '@shared/components/ui/Breadcrumb';
import { TatChip } from '@shared/components/ui/TatChip';

const STATUS_COLOR: Record<string, { bg: string; fg: string; border: string }> = {
  draft:           { bg: '#6b728015', fg: '#6b7280', border: '#6b728040' },
  awaiting_client: { bg: '#0EA5E915', fg: '#0EA5E9', border: '#0EA5E940' },
  submitted:       { bg: '#F59E0B15', fg: '#B45309', border: '#F59E0B40' },
  approved:        { bg: '#10B98115', fg: '#047857', border: '#10B98140' },
  rejected:        { bg: '#EF444415', fg: '#B91C1C', border: '#EF444440' },
  sourcing:        { bg: '#10B98115', fg: '#047857', border: '#10B98140' },
  filled:          { bg: '#14B8A615', fg: '#0F766E', border: '#14B8A640' },
  on_hold:         { bg: '#F59E0B15', fg: '#B45309', border: '#F59E0B40' },
  cancelled:       { bg: '#6b728015', fg: '#6b7280', border: '#6b728040' },
};

const PRIORITY_COLOR: Record<string, { bg: string; fg: string; border: string; label: string }> = {
  low:    { bg: '#6b728015', fg: '#6b7280', border: '#6b728040', label: 'Low' },
  normal: { bg: '#10B98115', fg: '#047857', border: '#10B98140', label: 'Normal' },
  high:   { bg: '#F59E0B15', fg: '#B45309', border: '#F59E0B40', label: 'High' },
  urgent: { bg: '#EF444415', fg: '#B91C1C', border: '#EF444440', label: 'Urgent' },
};

function inferRemoteOk(location: string | null): boolean {
  if (!location) return false;
  return /\bremote\b/i.test(location);
}

function Chip({
  label,
  bg,
  fg,
  border,
  className = '',
}: {
  label: string;
  bg: string;
  fg: string;
  border: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${className}`}
      style={{ background: bg, color: fg, borderColor: border }}
    >
      {label}
    </span>
  );
}

export interface RequisitionHeaderBandProps {
  req: Requisition;
  breadcrumb?: BreadcrumbItem[];
  actionsEnd?: ReactNode;
  /**
   * TAT target date for the gauge-less chip variant in the header. Falls
   * back to req.target_close_date. Pass `null` to hide the chip entirely.
   */
  tatTargetCloseDate?: string | null;
}

export function RequisitionHeaderBand({
  req,
  breadcrumb,
  actionsEnd,
  tatTargetCloseDate,
}: RequisitionHeaderBandProps) {
  const status = STATUS_COLOR[req.status] ?? STATUS_COLOR.draft!;
  const priority = PRIORITY_COLOR[req.priority ?? 'normal'] ?? PRIORITY_COLOR.normal!;
  const remoteOk = inferRemoteOk(req.location);
  const target =
    tatTargetCloseDate === undefined
      ? req.target_close_date
      : tatTargetCloseDate;

  return (
    <div className="border-b vscode-border">
      <div className="flex min-h-[56px] items-start justify-between gap-3 py-2 px-1">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {breadcrumb && breadcrumb.length > 0 && (
            <div className="text-[12px] vscode-text-tertiary leading-none">
              <Breadcrumb items={breadcrumb} />
            </div>
          )}
          <h1 className="truncate text-[15px] font-semibold leading-tight vscode-text-primary">
            {req.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] vscode-text-tertiary">
            {req.client ? (
              <Link to={`/clients/${req.client_id}`} className="hover:underline">
                {req.client.name}
              </Link>
            ) : (
              <span>Unassigned client</span>
            )}
            {req.department && <span aria-hidden>·</span>}
            {req.department && <span>{req.department}</span>}
          </div>

          {/* Chip cluster */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip
              label={REQUISITION_STATUS_LABELS[req.status] ?? req.status}
              bg={status.bg}
              fg={status.fg}
              border={status.border}
              className="capitalize"
            />
            <Chip
              label={`${priority.label} priority`}
              bg={priority.bg}
              fg={priority.fg}
              border={priority.border}
            />
            {req.employment_type && (
              <Chip
                label={String(req.employment_type).replace(/_/g, ' ')}
                bg="#F3F4F6"
                fg="#374151"
                border="#E5E7EB"
                className="capitalize"
              />
            )}
            {req.location && (
              <Chip
                label={req.location}
                bg="#F3F4F6"
                fg="#374151"
                border="#E5E7EB"
              />
            )}
            {remoteOk && (
              <Chip
                label="Remote OK"
                bg="#10B98115"
                fg="#047857"
                border="#10B98140"
              />
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {target !== null && (
            <TatChip targetCloseDate={target ?? null} closedAt={req.closed_at} />
          )}
          {actionsEnd && (
            <div className="flex flex-wrap items-center gap-2">{actionsEnd}</div>
          )}
        </div>
      </div>
    </div>
  );
}
